import { useState } from "react";
import { Plus, Scissors, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { MultiSelect } from "@/components/ui/MultiSelect";
import { useRegisterMutations } from "@/features/power_projects/hooks";
import type { CorrespondenceRegister } from "@/features/power_projects/types";

type Option = { value: string; label: string };

// One position-in-the-making: which of the source entry's files it takes + optional routing.
interface DraftPart {
  fileIds: Set<string>;
  targetUsers: string[];
  targetProject: string;
  note: string;
}

const emptyPart = (): DraftPart => ({
  fileIds: new Set(),
  targetUsers: [],
  targetProject: "",
  note: "",
});

// ── podział wpływu na pozycje — rozbij jeden wpływ na 2+ nowe pozycje rejestru ──
// Every part needs ≥ 1 file and every attachment of the source entry must land in at least
// one part (a file MAY be shared by several parts). Mirrors the backend split invariants.
export function SplitIncomingForm({
  row,
  userOptions,
  projectOptions,
  requireTargetUser = false,
  onDone,
}: {
  row: CorrespondenceRegister;
  userOptions: Option[];
  projectOptions: Option[];
  // Podział przez adresata dekretacji — every position must stay decreed to somebody
  // (the backend enforces the same rule and limits the choice to the current addressees).
  requireTargetUser?: boolean;
  onDone: () => void;
}) {
  const m = useRegisterMutations();
  const isFiled = row.status === "filed";
  const [parts, setParts] = useState<DraftPart[]>([emptyPart(), emptyPart()]);

  const updatePart = (idx: number, patch: Partial<DraftPart>) =>
    setParts((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));

  const toggleFile = (idx: number, fileId: string) =>
    setParts((prev) =>
      prev.map((p, i) => {
        if (i !== idx) return p;
        const next = new Set(p.fileIds);
        if (next.has(fileId)) next.delete(fileId);
        else next.add(fileId);
        return { ...p, fileIds: next };
      }),
    );

  const addPart = () => setParts((prev) => [...prev, emptyPart()]);
  const removePart = (idx: number) => setParts((prev) => prev.filter((_, i) => i !== idx));

  const assignedIds = new Set(parts.flatMap((p) => [...p.fileIds]));
  const unassigned = row.files.filter((f) => !assignedIds.has(f.id));
  const emptyParts = parts.some((p) => p.fileIds.size === 0);
  const unrouted = requireTargetUser && parts.some((p) => p.targetUsers.length === 0);
  const canSubmit = parts.length >= 2 && !emptyParts && !unrouted && unassigned.length === 0;

  const submit = () => {
    if (!canSubmit) return;
    m.splitIncoming.mutate(
      {
        id: row.id,
        parts: parts.map((p) => ({
          files: [...p.fileIds],
          target_users: p.targetUsers,
          target_project: p.targetProject || null,
          note: p.note,
        })),
      },
      { onSuccess: onDone },
    );
  };

  return (
    <div className="card p-3 mb-4 space-y-3 border-l-[3px] border-l-[var(--color-primary)]">
      <div className="flex items-center gap-1.5">
        <Scissors size={14} className="text-[var(--color-primary)]" />
        <h3 className="section-label">Podział wpływu na pozycje</h3>
      </div>
      <div className="text-[12px] text-[var(--color-text-secondary)]">
        <span className="font-medium text-[var(--color-text-primary)]">{row.counterparty_name || "—"}</span>
        {" · "}załączników: {row.files.length}. Wpływ zostanie zastąpiony poniższymi pozycjami — każda
        dostaje wskazane pliki i dalej żyje własnym cyklem (dekretacja, potwierdzenie odbioru).
        Ten sam plik może trafić do kilku pozycji.
        {isFiled && (
          <>
            {" "}Przesyłka jest już zadekretowana — każda pozycja zostaje zadekretowana na wskazane
            osoby, a odbiór potwierdza się osobno dla każdej z nich.
          </>
        )}
        {" "}Podział trafia do historii wpisu i dziennika zdarzeń (kto i jak podzielił).
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        {parts.map((part, idx) => (
          <div key={idx} className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2.5 space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="section-label text-[var(--color-text-secondary)]">Pozycja {idx + 1}</span>
              {parts.length > 2 && (
                <button
                  type="button"
                  title="Usuń pozycję"
                  className="text-[var(--color-text-muted)] hover:text-red-600"
                  onClick={() => removePart(idx)}
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
            <div className="space-y-1">
              <span className="section-label">Pliki</span>
              <ul className="space-y-0.5">
                {row.files.map((f) => (
                  <li key={f.id}>
                    <label className="flex items-start gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        className="mt-0.5 accent-[var(--color-primary)]"
                        checked={part.fileIds.has(f.id)}
                        onChange={() => toggleFile(idx, f.id)}
                      />
                      <span className="text-[11px] font-mono text-[var(--color-text-secondary)] break-all">
                        {f.original_filename}
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
              {part.fileIds.size === 0 && (
                <div className="section-label text-[var(--color-primary)]">Wybierz co najmniej jeden plik</div>
              )}
            </div>
            <div className="space-y-1">
              <span className="section-label">{requireTargetUser ? "Osoby" : "Osoby (opcjonalnie)"}</span>
              <MultiSelect
                options={userOptions}
                value={part.targetUsers}
                placeholder={requireTargetUser ? "— wskaż osobę —" : "— bez dekretacji —"}
                onChange={(v) => updatePart(idx, { targetUsers: v })}
              />
              {requireTargetUser && part.targetUsers.length === 0 && (
                <div className="section-label text-[var(--color-primary)]">Wskaż co najmniej jedną osobę</div>
              )}
            </div>
            <SearchableSelect
              label="Przedsięwzięcie (opcjonalnie)"
              options={projectOptions}
              value={part.targetProject}
              placeholder="— bez projektu —"
              onChange={(e) => updatePart(idx, { targetProject: e.target.value })}
            />
            <label className="block space-y-1">
              <span className="section-label">Adnotacja</span>
              <input
                className="input-base text-xs py-1.5"
                placeholder="np. dot. przyłącza Gniewino"
                value={part.note}
                onChange={(e) => updatePart(idx, { note: e.target.value })}
              />
            </label>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="ghost" onClick={addPart}>
          <Plus size={13} />
          Dodaj pozycję
        </Button>
        <Button size="sm" disabled={!canSubmit} loading={m.splitIncoming.isPending} onClick={submit}>
          <Scissors size={13} />
          Podziel na {parts.length} pozycje
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
        {unassigned.length > 0 && (
          <span className="section-label text-[var(--color-primary)]">
            Nieprzypisane pliki: {unassigned.map((f) => f.original_filename).join(", ")}
          </span>
        )}
        {m.splitIncoming.isError && (
          <span className="section-label text-red-600">Nie udało się podzielić wpływu. Spróbuj ponownie.</span>
        )}
      </div>
    </div>
  );
}
